// Readers for the parish-wide settings that ChurchConfig stores as JSON text
// (hidden modules, custom tile titles) and the fixed choices the Settings
// screen offers for the display formats. Kept apart from constants.ts, which
// is the module registry, so neither has to import the other.

import type { ChurchConfig } from '../types';

/** A JSON array of strings, or [] for anything blank or malformed. */
export const parseJsonStringArray = (raw?: string | null): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

/** A JSON object of string values, or {} for anything blank or malformed. */
export const parseJsonStringMap = (raw?: string | null): Record<string, string> => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => typeof v === 'string')
    ) as Record<string, string>;
  } catch {
    return {};
  }
};

/** Module ids the parish has taken off the dashboard. */
export const parseHiddenModules = (config?: Pick<ChurchConfig, 'hiddenModules'> | null): string[] =>
  parseJsonStringArray(config?.hiddenModules);

/** Module id -> the tile title this parish prefers. Blank titles are dropped. */
export const parseModuleTitles = (config?: Pick<ChurchConfig, 'moduleTitles'> | null): Record<string, string> =>
  Object.fromEntries(
    Object.entries(parseJsonStringMap(config?.moduleTitles))
      .map(([k, v]) => [k, v.trim()])
      .filter(([, v]) => v)
  );

/** A JSON object of positive whole numbers, or {} for anything blank or malformed. */
export const parseJsonNumberMap = (raw?: string | null): Record<string, number> => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => typeof v === 'number' && Number.isInteger(v) && v > 0)
    ) as Record<string, number>;
  } catch {
    return {};
  }
};

/** Category -> the position (1 shows first) this parish gave it on the dashboard and menus. */
export const parseCategoryOrder = (config?: Pick<ChurchConfig, 'categoryOrder'> | null): Record<string, number> =>
  parseJsonNumberMap(config?.categoryOrder);

/** Module id -> the position (1 shows first) this parish gave it within its category. */
export const parseModuleOrder = (config?: Pick<ChurchConfig, 'moduleOrder'> | null): Record<string, number> =>
  parseJsonNumberMap(config?.moduleOrder);

/**
 * Items in the order the parish chose: every numbered one first, by its
 * number (ties keep the given order), then the unnumbered ones in the given
 * order. Stable, so it is safe on an already-filtered list.
 */
export const sortByOrder = <T>(items: readonly T[], keyOf: (item: T) => string, order: Record<string, number>): T[] =>
  items
    .map((item, index) => ({ item, index, pos: order[keyOf(item)] }))
    .sort((a, b) => {
      if (a.pos != null && b.pos != null && a.pos !== b.pos) return a.pos - b.pos;
      if (a.pos != null && b.pos == null) return -1;
      if (a.pos == null && b.pos != null) return 1;
      return a.index - b.index;
    })
    .map(({ item }) => item);

export const sortCategories = <T extends string>(categories: readonly T[], categoryOrder: Record<string, number>): T[] =>
  sortByOrder(categories, (c) => c, categoryOrder);

export const sortModules = <T extends { id: string }>(modules: readonly T[], moduleOrder: Record<string, number>): T[] =>
  sortByOrder(modules, (m) => m.id, moduleOrder);

/**
 * The whole dashboard sequence: category by category in the parish's
 * category order, each category's modules in its module order. A module
 * whose category is not in the list keeps its place at the end.
 */
export const arrangeModules = <T extends { id: string; category: string }>(
  modules: readonly T[],
  categories: readonly string[],
  categoryOrder: Record<string, number>,
  moduleOrder: Record<string, number>
): T[] => {
  const grouped = sortCategories(categories, categoryOrder)
    .flatMap((category) => sortModules(modules.filter((m) => m.category === category), moduleOrder));
  const placed = new Set(grouped.map((m) => m.id));
  return [...grouped, ...modules.filter((m) => !placed.has(m.id))];
};

// --- Fixed choices offered by the Settings screen ---

export const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: '31/12/2026  (DD/MM/YYYY)' },
  { value: 'DD-MM-YYYY', label: '31-12-2026  (DD-MM-YYYY)' },
  { value: 'MM/DD/YYYY', label: '12/31/2026  (MM/DD/YYYY)' },
  { value: 'YYYY-MM-DD', label: '2026-12-31  (YYYY-MM-DD)' },
  { value: 'DD MMM YYYY', label: '31 Dec 2026  (DD MMM YYYY)' }
] as const;

export type DateFormat = (typeof DATE_FORMATS)[number]['value'];
export const DEFAULT_DATE_FORMAT: DateFormat = 'DD/MM/YYYY';

export const CURRENCIES = [
  { value: 'INR', label: '₹  Indian Rupee (INR)' },
  { value: 'USD', label: '$  US Dollar (USD)' },
  { value: 'GBP', label: '£  British Pound (GBP)' },
  { value: 'EUR', label: '€  Euro (EUR)' },
  { value: 'AED', label: 'د.إ  UAE Dirham (AED)' },
  { value: 'SGD', label: 'S$  Singapore Dollar (SGD)' },
  { value: 'MYR', label: 'RM  Malaysian Ringgit (MYR)' },
  { value: 'LKR', label: 'Rs  Sri Lankan Rupee (LKR)' },
  { value: 'AUD', label: 'A$  Australian Dollar (AUD)' },
  { value: 'CAD', label: 'C$  Canadian Dollar (CAD)' }
] as const;

export const DEFAULT_CURRENCY = 'INR';

export const THEME_CHOICES = [
  { value: 'light', label: '☀️ Light' },
  { value: 'dark', label: '🌙 Dark' }
] as const;

export const UI_SCALES = [
  { value: 'compact', label: 'Compact', hint: '90% — more rows on screen' },
  { value: 'normal', label: 'Normal', hint: '100%' },
  { value: 'large', label: 'Large', hint: '110% — easier to read' }
] as const;

export type UiScale = (typeof UI_SCALES)[number]['value'];

/** CSS zoom factor for each scale; unknown or blank means 1. */
export const uiScaleFactor = (scale?: string | null): number =>
  scale === 'compact' ? 0.9 : scale === 'large' ? 1.1 : 1;

/**
 * The three ways around the app. Every module works the same under each; the
 * choice is only how the menu is drawn — and it is a parish-wide setting so a
 * whole office sees one consistent screen.
 */
export const NAV_LAYOUTS = [
  { value: 'tiles', label: 'Tile dashboard',
    hint: 'Top bar and a home page of big module tiles. Best on phones and touch screens.' },
  { value: 'sidebar', label: 'Left sidebar',
    hint: 'A menu of every module down the left, grouped by category. One click between modules.' },
  { value: 'topmenu', label: 'Top menu bar',
    hint: 'A row of category menus under the top bar. Keeps the full width for tables.' }
] as const;

export type NavLayout = (typeof NAV_LAYOUTS)[number]['value'];
export const DEFAULT_NAV_LAYOUT: NavLayout = 'tiles';

export const navLayoutOf = (config?: Pick<ChurchConfig, 'navigationLayout'> | null): NavLayout =>
  NAV_LAYOUTS.some((l) => l.value === config?.navigationLayout)
    ? (config!.navigationLayout as NavLayout)
    : DEFAULT_NAV_LAYOUT;
